import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Tag, 
  Calendar, 
  MessageSquare, 
  Check, 
  ChevronRight, 
  ChevronLeft,
  Sparkles,
  Plus,
  TrendingDown,
  TrendingUp,
  Loader2,
  QrCode,
  Copy,
  ArrowRight,
  PartyPopper,
  Rocket,
  Trash2,
  Target,
  DollarSign,
  X,
  AlertTriangle,
  Upload,
  FileSpreadsheet,
  Wand2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { categoryApi, CreateCategoryRequest, recurringPaymentApi, ApiCategory, accountSettingsApi, transactionImportApi, InferredCategory } from "@/lib/api";
import { useUser } from "@/contexts/UserContext";
import { useExpense } from "@/contexts/ExpenseContext";
import { WHATSAPP_CONFIG } from "@/lib/constants";
import QRCode from "react-qr-code";

// WhatsApp brand icon
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkipWithoutCategories?: () => void;
}

interface SuggestedCategory {
  name: string;
  nameHe: string;
  color: string;
  type: 'expense' | 'income';
  selected: boolean;
}

const defaultCategories: SuggestedCategory[] = [
  { name: "Groceries", nameHe: "מכולת", color: "#4ECDC4", type: "expense", selected: true },
  { name: "Transportation", nameHe: "תחבורה", color: "#45B7D1", type: "expense", selected: true },
  { name: "Dining Out", nameHe: "מסעדות", color: "#FF6B6B", type: "expense", selected: true },
  { name: "Entertainment", nameHe: "בילויים", color: "#DDA0DD", type: "expense", selected: false },
  { name: "Shopping", nameHe: "קניות", color: "#FFEAA7", type: "expense", selected: false },
  { name: "Utilities", nameHe: "חשבונות", color: "#96CEB4", type: "expense", selected: true },
  { name: "Health", nameHe: "בריאות", color: "#FF8A80", type: "expense", selected: false },
  { name: "Salary", nameHe: "משכורת", color: "#82B1FF", type: "income", selected: true },
  { name: "Freelance", nameHe: "פרילנס", color: "#B39DDB", type: "income", selected: false },
];

const steps = [
  { id: 1, title: "Categories", icon: Tag },
  // { id: 2, title: "Budget", icon: Target }, // Hidden for now
  { id: 3, title: "Payments", icon: Calendar },
  { id: 4, title: "WhatsApp", icon: MessageSquare },
];

export function OnboardingWizard({ onComplete, onSkipWithoutCategories }: OnboardingWizardProps) {
  const { t, i18n } = useTranslation();
  const { user, syncBackendAccount } = useUser();
  const { refreshData } = useExpense();
  const isRTL = i18n.language === 'he';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [categories, setCategories] = useState<SuggestedCategory[]>(defaultCategories);
  const [customCategory, setCustomCategory] = useState({ name: "", color: "#4ECDC4", type: "expense" as const });
  const [isCreatingCategories, setIsCreatingCategories] = useState(false);
  const [createdCategories, setCreatedCategories] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showSkipConfirmDialog, setShowSkipConfirmDialog] = useState(false);
  
  // Final saving state - all data is created at the end
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [savingProgress, setSavingProgress] = useState<{
    step: 'categories' | 'budgets' | 'payments' | 'verifying' | 'complete';
    message: string;
  } | null>(null);
  
  // Category inference state
  const [isInferring, setIsInferring] = useState(false);
  const [inferredCategories, setInferredCategories] = useState<InferredCategory[]>([]);
  const [showInferredView, setShowInferredView] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // Recurring payments state
  const [availableCategories, setAvailableCategories] = useState<ApiCategory[]>([]);
  const [recurringPayments, setRecurringPayments] = useState<Array<{
    id: string;
    description: string;
    amount: string;
    categoryId: string;
    dayOfMonth: number;
  }>>([]);
  const [newPayment, setNewPayment] = useState({
    description: "",
    amount: "",
    categoryId: "",
    dayOfMonth: 1
  });
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [isSavingPayments, setIsSavingPayments] = useState(false);
  
  // Budget limits state
  const [accountBudgetLimit, setAccountBudgetLimit] = useState<string>("");
  const [categoryBudgets, setCategoryBudgets] = useState<Record<string, string>>({});
  const [isSavingBudgets, setIsSavingBudgets] = useState(false);

  // Calculate progress based on step index, not step ID
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;
  
  // Derive available categories from the selected categories in step 1
  // This is used for budgets and recurring payments dropdowns
  useEffect(() => {
    const selectedCategories = categories.filter(cat => cat.selected);
    const derivedCategories: ApiCategory[] = selectedCategories.map((cat, index) => ({
      id: `temp-${index}-${cat.name}`, // Temporary ID - will be replaced with real ID after creation
      accountId: '', // Not needed for display
      name: isRTL ? cat.nameHe : cat.name,
      color: cat.color,
      type: cat.type,
      monthlyBudgetLimit: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));
    setAvailableCategories(derivedCategories);
  }, [categories, isRTL]);

  // Handle file upload for category inference
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsInferring(true);
    try {
      const result = await transactionImportApi.inferCategories(file);
      
      if (result.success && result.categories.length > 0) {
        setInferredCategories(result.categories);
        // Convert inferred categories to the format used by the wizard
        const newCategories: SuggestedCategory[] = result.categories.map(cat => ({
          name: cat.nameEn,
          nameHe: cat.name,
          color: cat.color,
          type: cat.type,
          selected: cat.selected
        }));
        setCategories(newCategories);
        setShowInferredView(true);
        toast.success(
          t('onboarding.inferSuccess', 'Found {{count}} categories from your file!', { count: result.categories.length })
        );
      } else {
        toast.error(result.error || t('onboarding.inferError', 'Could not infer categories from file'));
      }
    } catch (error) {
      console.error('Failed to infer categories:', error);
      toast.error(t('onboarding.inferError', 'Could not infer categories from file'));
    } finally {
      setIsInferring(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Toggle inferred category selection
  const toggleInferredCategory = (index: number) => {
    setInferredCategories(prev => prev.map((cat, i) => 
      i === index ? { ...cat, selected: !cat.selected } : cat
    ));
    // Also update the main categories array
    setCategories(prev => prev.map((cat, i) => 
      i === index ? { ...cat, selected: !cat.selected } : cat
    ));
  };

  const toggleCategory = (index: number) => {
    setCategories(prev => prev.map((cat, i) => 
      i === index ? { ...cat, selected: !cat.selected } : cat
    ));
  };

  const addCustomCategory = () => {
    if (!customCategory.name.trim()) return;
    
    setCategories(prev => [...prev, {
      name: customCategory.name,
      nameHe: customCategory.name,
      color: customCategory.color,
      type: customCategory.type,
      selected: true
    }]);
    setCustomCategory({ name: "", color: "#4ECDC4", type: "expense" });
    toast.success(t('onboarding.categoryAdded', 'Category added!'));
  };

  // Move to next step - categories will be created at the end
  const handleContinueFromCategories = () => {
    const selectedCategories = categories.filter(c => c.selected);
    if (selectedCategories.length === 0) {
      toast.error(t('onboarding.selectAtLeastOne', 'Please select at least one category'));
      return;
    }
    setCurrentStep(3); // Skip budget step (step 2) for now
  };

  const getVerificationMessage = () => {
    const accountId = user?.backendAccount?.accountId || 'unknown';
    return `verify-agent-${accountId}`;
  };

  const generateWhatsAppURL = () => {
    const message = encodeURIComponent(getVerificationMessage());
    return `${WHATSAPP_CONFIG.whatsappUrl}?text=${message}`;
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(getVerificationMessage());
    toast.success(t('whatsappConfig.messageCopied', 'Message copied!'));
  };

  // Main function to save all data at the end
  const handleFinalSave = async () => {
    const selectedCategories = categories.filter(c => c.selected);
    
    // If no categories selected, just complete
    if (selectedCategories.length === 0) {
      localStorage.setItem('onboarding_skipped_without_categories', 'true');
    localStorage.setItem('onboarding_completed', 'true');
      onComplete();
      return;
    }
    
    setIsSavingAll(true);
    
    try {
      // Step 1: Create categories
      setSavingProgress({ 
        step: 'categories', 
        message: t('onboarding.creatingCategories', 'יוצר קטגוריות...') 
      });
      
      const createdCategoryIds: Record<string, string> = {}; // Map old temp index to new ID
      
      for (let i = 0; i < selectedCategories.length; i++) {
        const cat = selectedCategories[i];
        const categoryData: CreateCategoryRequest = {
          name: isRTL ? cat.nameHe : cat.name,
          color: cat.color,
          type: cat.type
        };
        const created = await categoryApi.create(categoryData);
        createdCategoryIds[i.toString()] = created.id;
        
        // Small delay for visual effect
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setCreatedCategories(true);
      
      // Step 2: Save budget limits
      if (accountBudgetLimit && parseFloat(accountBudgetLimit) > 0) {
        setSavingProgress({ 
          step: 'budgets', 
          message: t('onboarding.settingBudgets', 'מגדיר תקציבים...') 
        });
        
        await accountSettingsApi.updateSettings({
          monthlyBudgetLimit: parseFloat(accountBudgetLimit)
        });
        
        // Note: Category budgets would need to be mapped to new IDs
        // For now, we skip category-specific budgets in initial setup
        
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Step 3: Create recurring payments
      if (recurringPayments.length > 0) {
        setSavingProgress({ 
          step: 'payments', 
          message: t('onboarding.creatingPayments', 'יוצר תשלומים קבועים...') 
        });
        
        // Reload categories from API to get their IDs
        const apiCategories = await categoryApi.getAll();
        
        for (const payment of recurringPayments) {
          const expirationDate = new Date();
          expirationDate.setFullYear(expirationDate.getFullYear() + 1);
          
          // Find the category - payment.categoryId might be from availableCategories
          // which were loaded before. We need to match by name if IDs don't work
          let categoryId = payment.categoryId;
          const matchingCategory = apiCategories.find(c => c.id === payment.categoryId);
          if (!matchingCategory) {
            // Try to find by name
            const localCategory = availableCategories.find(c => c.id === payment.categoryId);
            if (localCategory) {
              const apiMatch = apiCategories.find(c => c.name === localCategory.name);
              if (apiMatch) categoryId = apiMatch.id;
            }
          }
          
          await recurringPaymentApi.create({
            paymentType: 'periodic',
            amount: parseFloat(payment.amount),
            categoryId: categoryId,
            description: payment.description,
            color: apiCategories.find(c => c.id === categoryId)?.color || '#4ECDC4',
            dayOfMonth: payment.dayOfMonth,
            firstPaymentDate: new Date().toISOString().split('T')[0],
            totalPayments: 12,
            expirationDate: expirationDate.toISOString().split('T')[0],
            isActive: true
          });
          
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // All saving done - mark onboarding as completed in the backend
      try {
        await accountSettingsApi.updateSettings({ onboardingCompleted: true });
      } catch (error) {
        console.error('Failed to update onboarding completion status:', error);
      }
      
      localStorage.setItem('onboarding_completed', 'true');
      setIsSavingAll(false);
      setSavingProgress(null);
      setShowCompletion(true);
      
      // Fetch all data in background while showing the completion animation
      await refreshData();
      
      // Wait for animation to play before navigating
    setTimeout(() => {
      onComplete();
      }, 3000);
      
    } catch (error) {
      console.error('Failed to save onboarding data:', error);
      toast.error(t('onboarding.saveFailed', 'שגיאה בשמירת הנתונים. נסה שנית.'));
      setIsSavingAll(false);
      setSavingProgress(null);
    }
  };
  
  const handleComplete = () => {
    handleFinalSave();
  };

  const handleSkipStep = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };
  
  const handleCloseWizard = () => {
    // If categories haven't been created, show confirmation dialog
    if (!createdCategories) {
      setShowSkipConfirmDialog(true);
    } else {
      // Categories exist, just complete
      handleComplete();
    }
  };
  
  const handleConfirmSkipWithoutCategories = () => {
    setShowSkipConfirmDialog(false);
    // Store that user skipped without categories
    localStorage.setItem('onboarding_skipped_without_categories', 'true');
    localStorage.setItem('onboarding_completed', 'true');
    if (onSkipWithoutCategories) {
      onSkipWithoutCategories();
    } else {
      onComplete();
    }
  };
  
  // Add a new recurring payment to the list
  const addPaymentToList = () => {
    if (!newPayment.description.trim() || !newPayment.amount || !newPayment.categoryId) {
      toast.error(t('onboarding.fillPaymentFields', 'Please fill in all payment fields'));
      return;
    }
    
    setRecurringPayments(prev => [...prev, {
      id: Date.now().toString(),
      ...newPayment
    }]);
    setNewPayment({ description: "", amount: "", categoryId: "", dayOfMonth: 1 });
    setIsAddingPayment(false);
    toast.success(t('onboarding.paymentAdded', 'Payment added to list!'));
  };
  
  // Remove a payment from the list
  const removePaymentFromList = (id: string) => {
    setRecurringPayments(prev => prev.filter(p => p.id !== id));
  };
  
  // Move to next step - payments will be created at the end
  const handleContinueFromPayments = () => {
      setCurrentStep(4);
  };
  
  const dayOptions = [
    { value: 1, label: isRTL ? '1 בחודש' : '1st' },
    { value: 5, label: isRTL ? '5 בחודש' : '5th' },
    { value: 10, label: isRTL ? '10 בחודש' : '10th' },
    { value: 15, label: isRTL ? '15 בחודש' : '15th' },
    { value: 20, label: isRTL ? '20 בחודש' : '20th' },
    { value: 25, label: isRTL ? '25 בחודש' : '25th' },
  ];
  
  // Move to next step - budgets will be applied at the end
  const handleContinueFromBudgets = () => {
      setCurrentStep(3);
  };
  
  // Update category budget
  const updateCategoryBudget = (categoryId: string, value: string) => {
    setCategoryBudgets(prev => ({
      ...prev,
      [categoryId]: value
    }));
  };

  // Saving progress overlay
  if (isSavingAll && savingProgress) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-primary/5" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center space-y-8 p-8 max-w-md">
          {/* Progress steps */}
          <div className="space-y-4">
            {/* Categories step */}
            <div className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 ${
              savingProgress.step === 'categories' 
                ? 'bg-primary/10 scale-105' 
                : savingProgress.step !== 'categories' && ['budgets', 'payments', 'complete'].includes(savingProgress.step)
                  ? 'bg-green-500/10 opacity-70'
                  : 'bg-muted/30 opacity-50'
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                savingProgress.step === 'categories' 
                  ? 'bg-primary text-primary-foreground animate-pulse' 
                  : ['budgets', 'payments', 'complete'].includes(savingProgress.step)
                    ? 'bg-green-500 text-white'
                    : 'bg-muted'
              }`}>
                {['budgets', 'payments', 'complete'].includes(savingProgress.step) ? (
                  <Check className="h-5 w-5" />
                ) : savingProgress.step === 'categories' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Tag className="h-5 w-5" />
                )}
              </div>
              <span className="font-medium">{t('onboarding.creatingCategories', 'יוצר קטגוריות...')}</span>
            </div>
            
            {/* Budgets step */}
            <div className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 ${
              savingProgress.step === 'budgets' 
                ? 'bg-primary/10 scale-105' 
                : ['payments', 'complete'].includes(savingProgress.step)
                  ? 'bg-green-500/10 opacity-70'
                  : 'bg-muted/30 opacity-50'
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                savingProgress.step === 'budgets' 
                  ? 'bg-primary text-primary-foreground animate-pulse' 
                  : ['payments', 'complete'].includes(savingProgress.step)
                    ? 'bg-green-500 text-white'
                    : 'bg-muted'
              }`}>
                {['payments', 'complete'].includes(savingProgress.step) ? (
                  <Check className="h-5 w-5" />
                ) : savingProgress.step === 'budgets' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Target className="h-5 w-5" />
                )}
              </div>
              <span className="font-medium">{t('onboarding.settingBudgets', 'מגדיר תקציבים...')}</span>
            </div>
            
            {/* Payments step */}
            <div className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 ${
              savingProgress.step === 'payments' 
                ? 'bg-primary/10 scale-105' 
                : savingProgress.step === 'complete'
                  ? 'bg-green-500/10 opacity-70'
                  : 'bg-muted/30 opacity-50'
            }`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                savingProgress.step === 'payments' 
                  ? 'bg-primary text-primary-foreground animate-pulse' 
                  : savingProgress.step === 'complete'
                    ? 'bg-green-500 text-white'
                    : 'bg-muted'
              }`}>
                {savingProgress.step === 'complete' ? (
                  <Check className="h-5 w-5" />
                ) : savingProgress.step === 'payments' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Calendar className="h-5 w-5" />
                )}
              </div>
              <span className="font-medium">{t('onboarding.creatingPayments', 'יוצר תשלומים קבועים...')}</span>
            </div>
            
            {/* Complete */}
            {savingProgress.step === 'complete' && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-green-500/10 scale-105 animate-in fade-in duration-300">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500 text-white">
                  <Sparkles className="h-5 w-5" />
                </div>
                <span className="font-medium text-green-600 dark:text-green-400">{t('onboarding.setupComplete', 'ההגדרה הושלמה!')}</span>
              </div>
            )}
          </div>
          
          {/* Current status message */}
          <p className="text-muted-foreground animate-pulse">
            {savingProgress.message}
          </p>
        </div>
      </div>
    );
  }

  // Completion animation screen - Full page with rocket
  if (showCompletion) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-blue-500/10 rounded-full blur-2xl animate-pulse delay-500" />
          </div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-primary/30 rounded-full animate-bounce"
              style={{
                left: `${10 + (i * 7)}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.2}s`,
                animationDuration: `${2 + (i % 3)}s`,
              }}
            />
          ))}
        </div>
        
        <div className="relative text-center space-y-8 px-6">
          {/* Rocket with launch animation */}
          <div className="relative">
            <div className="animate-in zoom-in duration-700">
              <div className="relative inline-block">
                {/* Rocket glow effect */}
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-2xl scale-150 animate-pulse" />
                
                {/* Main rocket */}
                <div className="relative animate-bounce" style={{ animationDuration: '2s' }}>
                  <Rocket className="h-32 w-32 sm:h-40 sm:w-40 mx-auto text-primary drop-shadow-2xl transform -rotate-45" />
                </div>
                
                {/* Rocket trail */}
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 rotate-45">
                  <div className="flex gap-1">
                    <div className="w-3 h-8 bg-gradient-to-t from-orange-500 to-yellow-300 rounded-full opacity-80 animate-pulse" />
                    <div className="w-2 h-6 bg-gradient-to-t from-orange-400 to-yellow-200 rounded-full opacity-60 animate-pulse delay-100" />
                    <div className="w-3 h-8 bg-gradient-to-t from-orange-500 to-yellow-300 rounded-full opacity-80 animate-pulse delay-200" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Confetti / Party elements */}
          <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <PartyPopper className="h-8 w-8 text-amber-500 animate-bounce opacity-70" style={{ animationDelay: '0.5s' }} />
          </div>
          
          {/* Title */}
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary via-indigo-600 to-primary bg-clip-text text-transparent animate-in slide-in-from-bottom duration-700 delay-300">
              {t('onboarding.allReady', 'הכל מוכן!')}
          </h1>
            <p className="text-lg sm:text-xl text-muted-foreground animate-in slide-in-from-bottom duration-700 delay-500 max-w-md mx-auto">
              {t('onboarding.readyToStart', 'מיד תועבר לדשבורד שלך')}
            </p>
          </div>
          
          {/* Loading indicator */}
          <div className="animate-in fade-in duration-700 delay-700">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('onboarding.preparingDashboard', 'מכין את הדשבורד שלך...')}</span>
            </div>
          </div>
          
          {/* Check marks for completed steps */}
          <div className="animate-in slide-in-from-bottom duration-700 delay-1000 flex flex-wrap justify-center gap-3 max-w-sm mx-auto">
            <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 px-3 py-1.5 rounded-full">
              <Check className="h-4 w-4" />
              <span>{t('onboarding.categoriesCreated', 'קטגוריות')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 px-3 py-1.5 rounded-full">
              <Check className="h-4 w-4" />
              <span>{t('onboarding.budgetsSet', 'תקציבים')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 px-3 py-1.5 rounded-full">
              <Check className="h-4 w-4" />
              <span>{t('onboarding.paymentsConfigured', 'תשלומים')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background"
        onClick={handleCloseWizard}
      >
        <X className="h-4 w-4 sm:h-5 sm:w-5" />
      </Button>
      
      <div className="w-full max-w-2xl h-[85vh] sm:h-[80vh] flex flex-col">
        {/* Header with progress - fixed at top */}
        <div className="shrink-0 mb-3 sm:mb-6">
          <div className="flex items-center justify-center gap-2 mb-2 sm:mb-4">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            <h1 className="text-xl sm:text-2xl font-bold text-center">
              {t('onboarding.title', 'Welcome to FINGENT!')}
            </h1>
          </div>
          <p className="text-center text-muted-foreground text-sm sm:text-base mb-3 sm:mb-6">
            {t('onboarding.subtitle', "Let's set up your account in just a few steps")}
          </p>
          
          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-3 sm:mb-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-all duration-300 ${
                      isActive ? 'scale-110' : ''
                    } ${
                      isCompleted || isActive 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-6 sm:w-12 h-1 mx-1 sm:mx-2 rounded transition-colors duration-300 ${
                      currentStep > step.id ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step content - scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto animate-in fade-in slide-in-from-right duration-300" key={currentStep}>
            {/* Step 1: Categories */}
            {currentStep === 1 && (
              <Card className="shadow-xl border-0 h-full flex flex-col">
                <CardHeader className="text-center pb-1 pt-1 sm:pt-2 shrink-0">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-1 sm:mb-1.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg flex items-center justify-center">
                    <Tag className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <CardTitle className="text-sm sm:text-base">
                    {t('onboarding.step1Title', 'Create Your Categories')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {t('onboarding.step1Description', 'Select categories to organize your expenses and income')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto space-y-2.5 sm:space-y-3 px-4 sm:px-6">
                  {/* AI Category Inference Option - Compact */}
                  <div 
                    className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 rounded-md p-1.5 sm:p-2 border border-purple-200/50 dark:border-purple-800/50"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="w-6 h-6 rounded bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shrink-0">
                        <Wand2 className="h-3 w-3 text-white" />
                      </div>
                      <div className="flex-1 min-w-0 text-start">
                        <span className="text-[10px] sm:text-[11px] font-medium text-purple-900 dark:text-purple-100 block">
                          {t('onboarding.inferTitleShort', 'AI categories from your transactions')}
                        </span>
                        <span className="text-xs sm:text-sm text-muted-foreground">
                          {t('onboarding.inferDescriptionShort', 'Upload credit card statement')}
                        </span>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="category-inference-file"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isInferring}
                        className="gap-1 bg-white dark:bg-slate-900 text-[10px] sm:text-xs shrink-0 h-6 px-2"
                      >
                        {isInferring ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-2.5 w-2.5" />
                            {t('onboarding.upload', 'Upload')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Show inferred results badge */}
                  {showInferredView && inferredCategories.length > 0 && (
                    <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/30 rounded-lg p-2 sm:p-3 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="text-xs sm:text-sm font-medium text-green-800 dark:text-green-200">
                          {t('onboarding.categoriesFromFile', '{{count}} categories detected from your file', { count: inferredCategories.length })}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowInferredView(false);
                          setCategories(defaultCategories);
                          setInferredCategories([]);
                        }}
                        className="text-xs h-7"
                      >
                        {t('onboarding.resetToDefault', 'Reset to default')}
                      </Button>
                    </div>
                  )}

                  {/* Suggested categories */}
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label className="text-xs sm:text-sm font-medium">
                      {showInferredView 
                        ? t('onboarding.detectedCategories', 'Detected Categories')
                        : t('onboarding.suggestedCategories', 'Suggested Categories')
                      }
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-1.5">
                      {categories.map((cat, index) => (
                        <button
                          key={index}
                          onClick={() => toggleCategory(index)}
                          className={`flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-md sm:rounded-lg border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                            cat.selected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-muted bg-background hover:border-muted-foreground/30'
                          }`}
                        >
                          <div 
                            className="w-3 h-3 sm:w-4 sm:h-4 rounded-full shrink-0"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="text-xs sm:text-sm font-medium truncate">
                            {isRTL ? cat.nameHe : cat.name}
                          </span>
                          {cat.type === 'income' ? (
                            <TrendingUp className="h-3 w-3 text-green-500 shrink-0 ml-auto" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-red-400 shrink-0 ml-auto" />
                          )}
                          {cat.selected && (
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Add custom category */}
                  <div className="space-y-1.5">
                    <Label className="text-xs sm:text-sm font-medium">
                      {t('onboarding.addCustom', 'Add Custom Category')}
                    </Label>
                    <div className="flex gap-1.5 sm:gap-2 flex-wrap sm:flex-nowrap">
                      <Input
                        placeholder={t('onboarding.categoryNamePlaceholder', 'Category name...')}
                        value={customCategory.name}
                        onChange={(e) => setCustomCategory(prev => ({ ...prev, name: e.target.value }))}
                        className="flex-1 min-w-[120px] h-8 sm:h-9 text-xs sm:text-sm"
                      />
                      <Select 
                        value={customCategory.type} 
                        onValueChange={(value: 'expense' | 'income') => setCustomCategory(prev => ({ ...prev, type: value }))}
                      >
                        <SelectTrigger className="w-[90px] sm:w-[110px] h-8 sm:h-9 text-xs sm:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="expense">
                            <div className="flex items-center gap-1.5">
                              <TrendingDown className="h-3 w-3 text-expense" />
                              <span>{t('categories.expense', 'הוצאה')}</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="income">
                            <div className="flex items-center gap-1.5">
                              <TrendingUp className="h-3 w-3 text-success" />
                              <span>{t('categories.income', 'הכנסה')}</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={addCustomCategory}
                        disabled={!customCategory.name.trim()}
                        className="h-8 w-8 sm:h-9 sm:w-9"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                </CardContent>
                {/* Fixed Actions Footer */}
                <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-2 sm:py-3">
                  <div className="flex justify-end">
                    <Button 
                      onClick={handleContinueFromCategories}
                      disabled={categories.filter(c => c.selected).length === 0}
                      className="gap-1.5 sm:gap-2 text-xs sm:text-sm"
                      size="sm"
                    >
                      {t('onboarding.continue', 'Continue')}
                      <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Step 2: Budget Limits - HIDDEN FOR NOW */}
            {/* currentStep === 2 && (
              ... Budget step content hidden ...
            ) */}

            {/* Step 3: Recurring Payments */}
            {currentStep === 3 && (
              <Card className="shadow-xl border-0 h-full flex flex-col">
                <CardHeader className="text-center pb-2 pt-4 sm:pt-6 shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-gradient-to-br from-blue-500/20 to-blue-500/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                    <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl">
                    {t('onboarding.step3RecurringTitle', 'Set Up Recurring Payments')}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {t('onboarding.step3RecurringDescription', 'Track subscriptions, rent, and regular bills automatically')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto space-y-4 sm:space-y-6 px-4 sm:px-6">
                  {/* Added payments list */}
                  {recurringPayments.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        {t('onboarding.yourPayments', 'Your Recurring Payments')}
                      </Label>
                      <div className="space-y-2">
                        {recurringPayments.map((payment) => {
                          const category = availableCategories.find(c => c.id === payment.categoryId);
                          return (
                            <div 
                              key={payment.id}
                              className="flex items-center justify-between p-2 sm:p-3 bg-muted/50 rounded-lg"
                            >
                              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div 
                                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full shrink-0"
                                  style={{ backgroundColor: category?.color || '#4ECDC4' }}
                                />
                                <div className="min-w-0">
                                  <p className="font-medium text-xs sm:text-sm truncate">{payment.description}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {category?.name} • {dayOptions.find(d => d.value === payment.dayOfMonth)?.label}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                <span className={`font-semibold text-xs sm:text-sm ${category?.type === 'income' ? 'text-success' : 'text-expense'}`}>
                                  {category?.type === 'income' 
                                    ? (isRTL ? `+₪${payment.amount}` : `+$${payment.amount}`)
                                    : (isRTL ? `-₪${payment.amount}` : `-$${payment.amount}`)
                                  }
                                </span>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-6 w-6 sm:h-7 sm:w-7 text-destructive"
                                  onClick={() => removePaymentFromList(payment.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Add payment form */}
                  {isAddingPayment ? (
                    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-muted/30 rounded-xl border-2 border-dashed border-muted">
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="col-span-2">
                          <Label className="text-[10px] sm:text-xs">{t('onboarding.paymentDescription', 'Description')}</Label>
                          <Input
                            placeholder={isRTL ? 'לדוגמה: שכירות, נטפליקס...' : 'e.g. Rent, Netflix...'}
                            value={newPayment.description}
                            onChange={(e) => setNewPayment(prev => ({ ...prev, description: e.target.value }))}
                            className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] sm:text-xs">{t('onboarding.paymentAmount', 'Amount')}</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={newPayment.amount}
                            onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                            className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] sm:text-xs">{t('onboarding.paymentDay', 'Day of Month')}</Label>
                          <Select
                            value={newPayment.dayOfMonth.toString()}
                            onValueChange={(value) => setNewPayment(prev => ({ ...prev, dayOfMonth: parseInt(value) }))}
                          >
                            <SelectTrigger className="mt-1 h-8 sm:h-9 text-xs sm:text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {dayOptions.map((day) => (
                                <SelectItem key={day.value} value={day.value.toString()}>
                                  {day.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] sm:text-xs">{t('onboarding.paymentCategory', 'Category')}</Label>
                          <Select
                            value={newPayment.categoryId}
                            onValueChange={(value) => setNewPayment(prev => ({ ...prev, categoryId: value }))}
                          >
                            <SelectTrigger className="mt-1 h-8 sm:h-9 text-xs sm:text-sm">
                              <SelectValue placeholder={t('onboarding.selectCategory', 'Select category')} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCategories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full" 
                                      style={{ backgroundColor: cat.color }}
                                    />
                                    <span className="text-xs sm:text-sm">{cat.name}</span>
                                    {cat.type === 'income' ? (
                                      <TrendingUp className="h-3 w-3 text-green-500" />
                                    ) : (
                                      <TrendingDown className="h-3 w-3 text-red-400" />
                                    )}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 text-xs sm:text-sm h-8 sm:h-9"
                          onClick={() => setIsAddingPayment(false)}
                        >
                          {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 text-xs sm:text-sm h-8 sm:h-9"
                          onClick={addPaymentToList}
                        >
                          <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          {t('onboarding.addPayment', 'Add')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="w-full border-dashed h-9 sm:h-10 text-xs sm:text-sm"
                      onClick={() => setIsAddingPayment(true)}
                    >
                      <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      {t('onboarding.addRecurringPayment', 'Add Recurring Payment')}
                    </Button>
                  )}
                  
                  {/* Info text */}
                  <p className="text-xs text-center text-muted-foreground">
                    {t('onboarding.addLater', 'You can set up recurring payments anytime from the menu')}
                  </p>
                </CardContent>
                {/* Fixed Actions Footer */}
                <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-2 sm:py-3">
                  <div className="flex justify-between">
                    <Button variant="ghost" onClick={() => setCurrentStep(1)} size="sm" className="text-xs sm:text-sm">
                      <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      {t('onboarding.back', 'Back')}
                    </Button>
                    <Button 
                      onClick={handleContinueFromPayments} 
                      className="gap-1.5 sm:gap-2 text-xs sm:text-sm"
                      size="sm"
                    >
                      {t('onboarding.continue', 'Continue')}
                      <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Step 4: WhatsApp */}
            {currentStep === 4 && (
              <Card className="shadow-xl border-0 h-full flex flex-col">
                <CardHeader className="text-center pb-2 pt-4 sm:pt-6 shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 bg-gradient-to-br from-green-500/20 to-green-500/10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl">
                    {t('onboarding.step4WhatsAppTitle', 'Connect WhatsApp')}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {t('onboarding.step4WhatsAppDescription', 'Track expenses by simply sending a message!')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto space-y-4 sm:space-y-6 px-4 sm:px-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {/* QR Code */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-xl p-3 sm:p-4 flex flex-col items-center">
                      <div className="flex items-center gap-2 mb-2 sm:mb-3">
                        <QrCode className="h-4 w-4 text-green-600" />
                        <span className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-400">
                          {t('onboarding.scanQR', 'Scan QR Code')}
                        </span>
                      </div>
                      <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm">
                        <div className="sm:hidden">
                          <QRCode value={generateWhatsAppURL()} size={100} level="M" />
                        </div>
                        <div className="hidden sm:block">
                          <QRCode value={generateWhatsAppURL()} size={140} level="M" />
                        </div>
                      </div>
                    </div>

                    {/* Manual instructions */}
                    <div className="bg-muted/50 rounded-xl p-3 sm:p-4" dir={isRTL ? 'rtl' : 'ltr'}>
                      <h4 className="font-medium mb-2 sm:mb-3 text-xs sm:text-sm text-start">
                        {t('onboarding.orManually', 'Or connect manually')}
                      </h4>
                      <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                        <div className="flex gap-2">
                          <span className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 bg-primary/10 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary">1</span>
                          <span className="text-muted-foreground">
                            {t('onboarding.manual1', 'Save')} <strong className="text-xs sm:text-sm" dir="ltr">{WHATSAPP_CONFIG.agentNumberWithPlus}</strong>
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 bg-primary/10 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold text-primary">2</span>
                          <span className="text-muted-foreground">
                            {t('onboarding.manual2', 'Send this message:')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-background border rounded-lg p-1.5 sm:p-2" dir="ltr">
                          <code className="flex-1 text-[10px] sm:text-xs font-mono text-primary truncate">
                            {getVerificationMessage()}
                          </code>
                          <Button variant="ghost" size="icon" className="h-5 w-5 sm:h-6 sm:w-6" onClick={handleCopyMessage}>
                            <Copy className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                          </Button>
                        </div>
                        {/* Open WhatsApp button inside manual section */}
                        <Button 
                          onClick={() => window.open(generateWhatsAppURL(), '_blank')}
                          className="w-full gap-2 bg-green-600 hover:bg-green-700 mt-2"
                          size="sm"
                        >
                          <WhatsAppIcon className="h-4 w-4" />
                          {t('onboarding.openWhatsApp', 'Open WhatsApp')}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Example messages */}
                  <div className="bg-muted/30 rounded-xl p-3 sm:p-4" dir={isRTL ? 'rtl' : 'ltr'}>
                    <h4 className="font-medium mb-1.5 sm:mb-2 text-xs sm:text-sm text-start">
                      {t('onboarding.exampleUsage', 'Example usage')}
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-start">
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {isRTL ? '"120 שקל מכולת"' : '"$50 groceries"'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {isRTL ? '"קפה 18 שקל"' : '"coffee $5"'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">
                        {isRTL ? '"סיכום חודשי"' : '"monthly summary"'}
                      </Badge>
                    </div>
                  </div>

                </CardContent>
                {/* Fixed Actions Footer */}
                <div className="shrink-0 border-t bg-card px-4 sm:px-6 py-2 sm:py-3">
                  <div className="flex justify-between">
                    <Button variant="ghost" onClick={() => setCurrentStep(3)} size="sm" className="text-xs sm:text-sm">
                      <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                      {t('onboarding.back', 'Back')}
                    </Button>
                    <Button 
                      onClick={handleComplete}
                      className="gap-2 text-xs sm:text-sm"
                      size="sm"
                    >
                      {t('onboarding.finish', 'Finish')}
                      <Check className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}
        </div>
      </div>
    </div>
    
    {/* Skip confirmation dialog */}
    <AlertDialog open={showSkipConfirmDialog} onOpenChange={setShowSkipConfirmDialog}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            </div>
            <AlertDialogTitle className="text-lg">
              {t('onboarding.skipConfirmTitle', 'Skip Setup?')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-sm">
            {t('onboarding.skipConfirmDescription', "You haven't created any categories yet. Without categories, you won't be able to track expenses or use WhatsApp integration. You can always set up categories later from the settings.")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel className="text-sm">
            {t('onboarding.continueSetup', 'Continue Setup')}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmSkipWithoutCategories}
            className="bg-amber-600 hover:bg-amber-700 text-sm"
          >
            {t('onboarding.skipAnyway', 'Skip Anyway')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

